import { Link } from '@elsa/ui';
import type { ReactNode } from 'react';
import { OPERATOR } from '../config.ts';

// Paginile legale. PROIECTE: de revizuit cu un jurist si de completat datele operatorului
// (src/config.ts, OPERATOR) inainte de lansare (docs/22 §7).

function Doc({
  title,
  updated,
  children,
}: {
  title: string;
  updated: string;
  children: ReactNode;
}) {
  return (
    <article className="prose-legal mx-auto max-w-3xl px-4 py-10">
      <h1 className="text-3xl leading-tight font-extrabold tracking-tight sm:text-4xl">
        {title}
        <span className="text-semnal">.</span>
      </h1>
      <p className="mt-1 text-xs text-gri-700">Ultima actualizare: {updated}</p>
      <div className="mt-4 border-l-4 border-semnal bg-gri-200 px-3 py-2 text-xs text-cerneala">
        Document în lucru, înainte de lansarea comercială. Versiunea finală se publică aici, cu data
        intrării în vigoare.
      </div>
      {children}
    </article>
  );
}

const op = () => (
  <>
    <b>{OPERATOR.name}</b>, CUI {OPERATOR.cui}, cu sediul în {OPERATOR.address}, email{' '}
    {OPERATOR.email}
  </>
);

export function TermsPage() {
  return (
    <Doc title="Termeni și condiții" updated="31.10.2026">
      <h2>1. Cine suntem și ce oferim</h2>
      <p>
        Elsa este un serviciu online operat de {op()} („noi”). Elsa adună și organizează achizițiile
        directe publicate în Sistemul Electronic de Achiziții Publice (SEAP, e-licitatie.ro) și
        oferă căutare, statistici de preț, alerte și exporturi.
      </p>
      <h2>2. Doar pentru firme</h2>
      <p>
        Serviciul se adresează exclusiv persoanelor juridice și altor entități cu cod fiscal (B2B).
        Contul aparține firmei identificate prin CUI; cel care o înregistrează declară că are
        dreptul să o reprezinte. O firmă poate avea un singur cont, cu mai mulți utilizatori.
      </p>
      <h2>3. Perioada de probă și abonamentul</h2>
      <ul>
        <li>Fiecare firmă nouă primește o perioadă de probă gratuită de 14 zile, fără card.</li>
        <li>
          După probă, accesul continuă doar cu un abonament plătit. Prețurile, drepturile fiecărui
          plan (număr de utilizatori, istoric, alerte, exporturi) și TVA-ul sunt afișate în
          aplicație înainte de plată.
        </li>
        <li>
          Plata se face prin Stripe, cu card. Abonamentul se reînnoiește automat la sfârșitul
          fiecărei perioade, până la anulare. Anularea se face oricând din portalul de plată și are
          efect la sfârșitul perioadei plătite.
        </li>
        <li>Factura fiscală se emite electronic și se transmite în sistemul RO e-Factura.</li>
      </ul>
      <h2>4. Utilizare permisă</h2>
      <ul>
        <li>Nu revinzi, nu republici în masă și nu extragi automat datele din Elsa.</li>
        <li>Nu împarți contul cu persoane din afara firmei; fiecare coleg are utilizatorul lui.</li>
        <li>Nu încerci să ocolești limitele planului sau măsurile de securitate.</li>
      </ul>
      <h2>5. Datele SEAP</h2>
      <p>
        Datele provin din surse publice și sunt prezentate așa cum au fost publicate. Ne străduim să
        le extragem complet și corect, dar nu garantăm că sunt fără erori, complete sau actuale.
        Decizia de a participa la o achiziție îți aparține; verifică întotdeauna anunțul original în
        SEAP.
      </p>
      <h2>6. Răspundere</h2>
      <p>
        În limitele legii, răspunderea noastră totală pentru orice prejudiciu legat de serviciu este
        limitată la sumele plătite de firmă în ultimele 12 luni. Nu răspundem pentru pierderi
        indirecte (profit nerealizat, oportunități pierdute).
      </p>
      <h2>7. Suspendare și încetare</h2>
      <p>
        Putem suspenda accesul în caz de încălcare a acestor termeni sau de neplată. Poți închide
        contul oricând din aplicație (Cont → Șterge contul).
      </p>
      <h2>8. Date personale</h2>
      <p>
        Prelucrarea datelor personale e descrisă în{' '}
        <Link to="/confidentialitate" className="underline">
          politica de confidențialitate
        </Link>
        .
      </p>
      <h2>9. Legea aplicabilă</h2>
      <p>
        Acești termeni sunt guvernați de legea română. Litigiile se soluționează pe cale amiabilă
        sau, în lipsa unei înțelegeri, de instanțele competente de la sediul nostru.
      </p>
    </Doc>
  );
}

export function PrivacyPage() {
  return (
    <Doc title="Politica de confidențialitate" updated="31.10.2026">
      <h2>1. Operatorul</h2>
      <p>Operatorul datelor personale este {op()}.</p>
      <h2>2. Ce date prelucrăm și de ce</h2>
      <ul>
        <li>
          <b>Contul tău</b> (nume, email, parolă criptată, funcția în firmă, jurnalul de
          autentificare): pentru a-ți oferi serviciul (executarea contractului, art. 6 alin. (1)
          lit. b GDPR) și pentru securitate (interes legitim, lit. f).
        </li>
        <li>
          <b>Datele firmei</b> (denumire, CUI, adresă, email de facturare) și <b>datele de plată</b>
          (procesate de Stripe; noi nu vedem numărul cardului): pentru abonament și facturare
          (contract și obligație legală, lit. b și c).
        </li>
        <li>
          <b>Ce faci în aplicație</b> (alerte, căutări salvate, exporturi, notificări): pentru
          funcțiile pe care le folosești și pentru plafoanele planului.
        </li>
        <li>
          <b>Datele din SEAP</b>: achizițiile publice pot conține date ale unor persoane fizice (de
          exemplu furnizori PFA sau persoane de contact). Le preluăm din sursa publică pentru
          transparența cheltuirii banului public (interes legitim, lit. f). Persoana vizată se poate
          opune (§5).
        </li>
      </ul>
      <h2>3. Cui le transmitem</h2>
      <p>Folosim furnizori care prelucrează datele în numele nostru, pe bază de contract:</p>
      <ul>
        <li>Supabase (baza de date și autentificarea; servere în UE, Frankfurt);</li>
        <li>Cloudflare (găzduirea aplicației, protecția anti-robot);</li>
        <li>Stripe (plata cu cardul);</li>
        <li>Resend (trimiterea emailurilor);</li>
        <li>furnizorul de facturare electronică (emiterea facturilor în RO e-Factura).</li>
      </ul>
      <p>
        Unii furnizori pot transfera date în afara SEE; transferul se face cu garanțiile prevăzute
        de GDPR (decizii de adecvare sau clauze contractuale standard).
      </p>
      <h2>4. Cât timp le păstrăm</h2>
      <ul>
        <li>contul: cât timp e activ; la ștergere, datele contului se șterg imediat;</li>
        <li>
          documentele de facturare: cât cere legea (în prezent, 10 ani), la furnizorul de facturare;
        </li>
        <li>jurnalele tehnice: cel mult 90 de zile;</li>
        <li>copiile de siguranță: cel mult 30 de zile.</li>
      </ul>
      <h2>5. Drepturile tale</h2>
      <p>
        Ai dreptul de acces, rectificare, ștergere, restricționare, portabilitate și opoziție. Din
        aplicație poți descărca oricând datele tale (Cont → Descarcă datele mele) și poți șterge
        contul. Pentru orice altă cerere scrie-ne la {OPERATOR.email}. Ai și dreptul să depui o
        plângere la Autoritatea Națională de Supraveghere a Prelucrării Datelor cu Caracter Personal
        (ANSPDCP).
      </p>
      <h2>6. Securitate</h2>
      <p>
        Datele sunt criptate în tranzit, accesul e separat pe firme la nivelul bazei de date, iar
        copiile de siguranță sunt criptate.
      </p>
    </Doc>
  );
}

export function CookiesPage() {
  return (
    <Doc title="Cookie-uri și stocare locală" updated="31.10.2026">
      <p>
        Elsa nu folosește cookie-uri de publicitate sau de analiză. În browserul tău păstrăm doar ce
        e strict necesar ca aplicația să funcționeze:
      </p>
      <ul>
        <li>sesiunea de autentificare (stocare locală, cheia Supabase), cât timp ești în cont;</li>
        <li>firma aleasă, dacă ești în mai multe firme (stocare locală).</li>
      </ul>
      <p>
        La înregistrare, Cloudflare Turnstile poate folosi stocare proprie pentru verificarea
        anti-robot. La plată ești dus pe pagina Stripe, care își folosește propriile cookie-uri,
        descrise în politica Stripe.
      </p>
      <p>
        Pentru aceste elemente strict necesare nu e nevoie de consimțământ (Legea 506/2004, art. 4
        alin. (6)).
      </p>
    </Doc>
  );
}
